import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ArrowRight, 
  QuestionMarkCircle, 
  HelpCircle,
  User, 
  Wifi, 
  Gauge, 
  Bell, 
  Search, 
  Menu, 
  X, 
  ChevronDown, 
  Globe,
  Moon,
  Sun,
  MessageSquare,
  Shield,
  Settings,
  AlertTriangle
} from 'lucide-react';
import NetworkTestTab from './CognitoNet/NetworkTestTab';
import SpeedTestTab from './CognitoNet/SpeedTestTab';
import Avatar from '../../services/Avatar';

const CognitoNet = () => {
  const [showHelpPopup, setShowHelpPopup] = useState(false);
  const [showProfilePopup, setShowProfilePopup] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [activeTab, setActiveTab] = useState('network-test');
  const [currentUser, setCurrentUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [scrollPosition, setScrollPosition] = useState(0);
  const [darkMode, setDarkMode] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [networkStatus, setNetworkStatus] = useState({
    status: 'online',
    lastChecked: new Date(),
    latency: 24
  });

     useEffect(() => {
        const controller = new AbortController();
        const fetchUserProfile = async () => {
            try {
                const response = await axios.get('/users/me', { 
                    withCredentials: true,
                    signal: controller.signal // Attach signal to cancel duplicate requests
                });
                setCurrentUser(response.data);
            } catch (error) {
                if (axios.isCancel(error)) {
                    console.log('Request canceled:', error.message);
                } else {
                    console.error('Error fetching user profile:', error);
                }
            }
        };
    
        fetchUserProfile();
    
        return () => controller.abort(); // Cleanup function to cancel request on re-render
    }, []);

  // Handle scroll effects for navbar
  useEffect(() => {
    const handleScroll = () => {
      setScrollPosition(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Update time every minute
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000);
    
    return () => {
      clearInterval(timer);
    };
  }, []);

  // Simulate occasional network status checks
  useEffect(() => {
    const networkCheck = setInterval(() => {
      const statuses = ['online', 'online', 'online', 'degraded'];
      const randomLatency = Math.floor(Math.random() * 100) + 10;
      
      setNetworkStatus({
        status: statuses[Math.floor(Math.random() * statuses.length)],
        lastChecked: new Date(),
        latency: randomLatency
      });
    }, 30000);
    
    return () => {
      clearInterval(networkCheck);
    };
  }, []);

  const toggleHelpPopup = () => {
    setShowHelpPopup(!showHelpPopup);
    setShowProfilePopup(false);
    setShowNotifications(false);
  };

  const toggleProfilePopup = () => {
    setShowProfilePopup(!showProfilePopup);
    setShowHelpPopup(false);
    setShowNotifications(false);
  };

  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    setShowHelpPopup(false);
    setShowProfilePopup(false);
  };

  const toggleMobileMenu = () => {
    setShowMobileMenu(!showMobileMenu);
  };

  
 const handleLogout = async () => {
  try {
    const response = await fetch('/d-auth/logout', {
      method: 'POST', // or 'GET', depending on your backend implementation
      credentials: 'include', // include cookies with the request
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (response.ok) {       
      
      window.location.href = '/'; 
    } else {
      // If there's an error during logout
      const errorData = await response.json();
      //console.error('Logout failed', errorData);
      alert( errorData ||'Logout failed. Please try again.');
    }
  } catch (error) {
    //console.error('Error during logout:', error);
    alert('An error occurred. Please try again.');
  }
};

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const Skeleton = ({ width = "w-8", height = "h-8", rounded = "rounded-full" }) => (
    <div className={`${width} ${height} ${rounded} bg-gray-300 animate-pulse`} />
  );

  const notifications = [
    { id: 1, type: 'alert', message: 'Unusual network activity detected', time: '2 hours ago' },
    { id: 2, type: 'info', message: 'Weekly network report available', time: '1 day ago' },
    { id: 3, type: 'success', message: 'Speed test completed successfully', time: '3 days ago' }
  ];

  const mainBgColor = darkMode ? 'bg-gray-900' : 'bg-gray-100';
  const navBgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const textColor = darkMode ? 'text-gray-100' : 'text-gray-800';
  const mutedTextColor = darkMode ? 'text-gray-400' : 'text-gray-500';
  const highlightColor = 'text-blue-400';
  const cardBgColor = darkMode ? 'bg-gray-800' : 'bg-white';
  const borderColor = darkMode ? 'border-gray-700' : 'border-gray-200';
  const hoverBgColor = darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200';

  return (
    <div className={`flex flex-col min-h-screen ${mainBgColor} ${textColor} transition-colors duration-300`}>
      {/* Fixed Navbar */}
      <nav className={`fixed top-0 left-0 right-0 ${navBgColor} shadow-md z-50 transition-all duration-300 ${scrollPosition > 50 ? 'py-2' : 'py-3'}`}>
        <div className="container mx-auto px-4">
          <div className="flex justify-between items-center">
            {/* Logo and Search Section */}
            <div className="flex items-center space-x-4">
              {/* Mobile Menu Button */}
              <button onClick={toggleMobileMenu} className="md:hidden p-2 rounded-full hover:bg-opacity-10 hover:bg-blue-500">
                {showMobileMenu ? <X size={22} /> : <Menu size={22} />}
              </button>
              
              {/* Logo with animated gradient */}
              <div className="flex items-center space-x-2">
                <div className="h-8 w-8 rounded-md bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                  <Globe size={18} className="text-white" />
                </div>
                <span className={`font-bold text-xl ${highlightColor} tracking-tight`}>CognitoNet</span>
              </div>
              
              {/* Search Bar */}
              <div className="hidden md:flex items-center relative">
                <div className={`flex items-center rounded-full px-3 py-1 border ${borderColor} focus-within:border-blue-400 transition-colors`}>
                  <Search size={16} className={mutedTextColor} />
                  <input 
                    type="text" 
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search tools..." 
                    className={`ml-2 bg-transparent border-none focus:outline-none text-sm w-40 lg:w-64 ${textColor}`}
                  />
                </div>
              </div>
            </div>
            
            {/* Navigation Tabs */}
            <div className="hidden md:flex space-x-1">
              <button 
                onClick={() => setActiveTab('network-test')}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md transition-colors ${activeTab === 'network-test' ? `${cardBgColor} ${highlightColor}` : hoverBgColor}`}
              >
                <Wifi size={18} />
                <span>Network Test</span>
              </button>
              <button
                onClick={() => setActiveTab('speed-test')}
                className={`flex items-center space-x-1 px-3 py-2 rounded-md transition-colors ${activeTab === 'speed-test' ? `${cardBgColor} ${highlightColor}` : hoverBgColor}`}
              >
                <Gauge size={18} />
                <span>Speed Test</span>
              </button>
              <div className="group relative">
                <button className={`flex items-center space-x-1 px-3 py-2 rounded-md transition-colors ${hoverBgColor}`}>
                  <span>More Tools</span>
                  <ChevronDown size={16} />
                </button>
                <div className={`absolute top-full right-0 w-48 mt-1 ${cardBgColor} rounded-md shadow-lg overflow-hidden border ${borderColor} opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 transform origin-top-right`}>
                  <div className="py-1">
                    <a href="#" className={`block px-4 py-2 text-sm ${hoverBgColor} cursor-pointer`}>Security Scanner</a>
                    <a href="#" className={`block px-4 py-2 text-sm ${hoverBgColor} cursor-pointer`}>DNS Lookup</a>
                    <a href="#" className={`block px-4 py-2 text-sm ${hoverBgColor} cursor-pointer`}>Port Scanner</a>
                    <a href="#" className={`block px-4 py-2 text-sm ${hoverBgColor} cursor-pointer`}>Traffic Analyzer</a>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side Icons with Badges */}
            <div className="flex items-center space-x-1 lg:space-x-2">
              {/* Theme Toggle */}
              <button 
                onClick={toggleDarkMode}
                className={`p-2 rounded-full ${hoverBgColor} transition-colors relative hidden md:flex`}
                aria-label="Toggle dark mode"
              >
                {darkMode ? <Sun size={20} /> : <Moon size={20} />}
              </button>
              
              {/* Network Status Indicator */}
              <div className="hidden md:flex items-center">
                <div className={`p-2 rounded-md ${hoverBgColor} flex items-center transition-colors`}>
                  <div className={`h-2 w-2 rounded-full mr-2 ${networkStatus.status === 'online' ? 'bg-green-400' : 'bg-yellow-400'} animate-pulse`}></div>
                  <span className="text-xs hidden lg:inline">{networkStatus.latency}ms</span>
                </div>
              </div>
              
              {/* Notifications */}
              <button
                onClick={toggleNotifications}
                className={`p-2 rounded-full ${hoverBgColor} transition-colors relative`}
                aria-label="Notifications"
              >
                <Bell size={20} />
                <span className="absolute top-0 right-0 h-4 w-4 rounded-full bg-red-500 flex items-center justify-center text-white text-xs">
                  {notifications.length}
                </span>
              </button>
              
              {/* Help Button */}
              <button
                onClick={toggleHelpPopup}
                className={`p-2 rounded-full ${hoverBgColor} transition-colors relative`}
                aria-label="Help"
              >
                <HelpCircle size={20} />
              </button>
              
              {/* User Profile */}
              <button
                onClick={toggleProfilePopup}
                className={`p-2 rounded-full ${hoverBgColor} transition-colors relative`}
                aria-label="User profile"
              >
                <div className="h-6 w-6 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xs">
                  {currentUser ? (
                                       <>
                                       <div className="h-8 w-8 rounded-full bg-blue-500  items-center justify-center">
                                         <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                                       </div>                                      
                                     </>
                                    ) : (
                                      <div className="flex items-center gap-2">
                                        <Skeleton /> {/* Skeleton for Avatar */}
                                        <div className="flex flex-col">
                                          <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                          <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                        </div>
                                      </div>
                                    )}
                </div>
              </button>
              
              {/* Logout */}
              <button 
                onClick={handleLogout}
                className={`md:ml-2 p-2 rounded-full ${hoverBgColor} transition-colors`}
                aria-label="Logout"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      <div className={`fixed left-0 top-0 h-full w-64 ${cardBgColor} z-40 shadow-xl transform transition-transform duration-300 ease-in-out ${showMobileMenu ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 border-b ${borderColor}">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="h-8 w-8 rounded-md bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                <Globe size={18} className="text-white" />
              </div>
              <span className={`font-bold text-xl ${highlightColor}`}>CognitoNet</span>
            </div>
            <button onClick={toggleMobileMenu} className="p-1">
              <X size={20} />
            </button>
          </div>
        </div>
        <div className="py-2">
          <div className="px-4 py-3 border-b ${borderColor}">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white">
              {currentUser ? (
                     <>
                     <div className="h-8 w-8 rounded-full bg-blue-500  items-center justify-center">
                       <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                     </div>                    
                   </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Skeleton /> {/* Skeleton for Avatar */}
                      <div className="flex flex-col">
                        <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                        <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                      </div>
                    </div>
                  )}
              </div>
              <div>
                {currentUser ? (
                                     <>
                                     
                                     <div className="flex flex-col">
                                       <span className="text-sm font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                                       <span className="text-xs text-gray-400">{currentUser.role}</span>
                                     </div>
                                   </>
                                  ) : (
                                    <div className="flex items-center gap-2">
                                      <Skeleton /> {/* Skeleton for Avatar */}
                                      <div className="flex flex-col">
                                        <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                                        <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                                      </div>
                                    </div>
                                  )}
              </div>
            </div>
          </div>
          <div className="py-2">
            <button 
              onClick={() => {
                setActiveTab('network-test');
                toggleMobileMenu();
              }}
              className={`flex items-center space-x-3 w-full px-4 py-2 ${activeTab === 'network-test' ? highlightColor : ''}`}
            >
              <Wifi size={18} />
              <span>Network Test</span>
            </button>
            <button 
              onClick={() => {
                setActiveTab('speed-test');
                toggleMobileMenu();
              }}
              className={`flex items-center space-x-3 w-full px-4 py-2 ${activeTab === 'speed-test' ? highlightColor : ''}`}
            >
              <Gauge size={18} />
              <span>Speed Test</span>
            </button>
            <button className="flex items-center space-x-3 w-full px-4 py-2">
              <Shield size={18} />
              <span>Security Scanner</span>
            </button>
            <button className="flex items-center space-x-3 w-full px-4 py-2">
              <Globe size={18} />
              <span>DNS Lookup</span>
            </button>
          </div>
          <div className="px-4 py-2 border-t ${borderColor}">
            <button className="flex items-center space-x-3 w-full py-2">
              <Settings size={18} />
              <span>Settings</span>
            </button>
            <button className="flex items-center space-x-3 w-full py-2">
              <HelpCircle size={18} />
              <span>Help Center</span>
            </button>
            <button onClick={toggleDarkMode} className="flex items-center space-x-3 w-full py-2">
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
              <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
            </button>
            <button className="flex items-center space-x-3 w-full py-2">
              <ArrowRight size={18} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </div>

      {/* Help Popup */}
      {showHelpPopup && (
        <div className={`fixed top-16 right-24 w-72 ${cardBgColor} rounded-lg shadow-lg z-20 overflow-hidden border ${borderColor}`}>
          <div className="p-4">
            <h3 className="font-semibold text-lg mb-2">Help Center</h3>
            <div className={`mb-3 p-3 rounded-md bg-opacity-10 bg-blue-500 ${mutedTextColor}`}>
              <p className="text-sm">Need assistance with CognitoNet?</p>
            </div>
            <div className="space-y-2">
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <MessageSquare size={16} />
                <span>Live Chat Support</span>
              </a>
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <Search size={16} />
                <span>Search Documentation</span>
              </a>
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <HelpCircle size={16} />
                <span>Frequently Asked Questions</span>
              </a>
            </div>
            <div className="mt-4 pt-3 border-t ${borderColor}">
              <a href="#" className={`text-xs ${highlightColor} hover:underline`}>Contact our technical support team →</a>
            </div>
          </div>
        </div>
      )}

      {/* Profile Popup */}
      {showProfilePopup && (
        <div className={`fixed top-16 right-16 w-72 ${cardBgColor} rounded-lg shadow-lg z-20 overflow-hidden border ${borderColor}`}>
          <div className="p-4">
            <div className="flex items-center space-x-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                <span className="font-semibold text-white">
                <div className="flex items-center gap-2">
                  {currentUser ? (
                     <>
                     <div className="h-8 w-8 rounded-full bg-blue-500  items-center justify-center">
                       <Avatar firstName={currentUser.first_name} lastName={currentUser.last_name} />
                     </div>                    
                   </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Skeleton /> {/* Skeleton for Avatar */}
                      <div className="flex flex-col">
                        <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                        <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                      </div>
                    </div>
                  )}
                </div>
                </span>
              </div>
              <div>
              <div className="flex items-center gap-2">
                  {currentUser ? (
                     <>                     
                     <div className="flex flex-col">
                       <span className="text-sm font-medium">{currentUser.first_name} {currentUser.last_name}</span>
                       <span className="text-xs text-gray-400">{currentUser.role}</span>
                     </div>
                   </>
                  ) : (
                    <div className="flex items-center gap-2">
                      <Skeleton /> {/* Skeleton for Avatar */}
                      <div className="flex flex-col">
                        <Skeleton width="w-20" height="h-3" rounded="rounded-md" /> {/* Skeleton for Name */}
                        <Skeleton width="w-16" height="h-3" rounded="rounded-md" /> {/* Skeleton for Role */}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex items-center mt-1">
                  <div className="h-2 w-2 rounded-full bg-green-400 mr-1"></div>
                  <span className="text-xs text-green-400">Online</span>
                </div>
              </div>
            </div>
            <div className={`p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-200'} mb-3`}>
              <div className="flex justify-between items-center">
                <div>
                  <p className="text-xs font-medium">Account Status</p>
                  <p className={`text-xs mt-1 ${highlightColor}`}>Premium Plan</p>
                </div>
                <div className={`px-2 py-1 rounded-full text-xs ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-700'}`}>
                  Active
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <User size={16} />
                <span className="text-sm">My Profile</span>
              </a>
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <Settings size={16} />
                <span className="text-sm">Account Settings</span>
              </a>
              <a href="#" className={`flex items-center space-x-2 p-2 rounded-md ${hoverBgColor}`}>
                <Shield size={16} />
                <span className="text-sm">Security</span>
              </a>
              <a href="#" className={`flex items-center justify-between p-2 rounded-md ${hoverBgColor}`}>
                <div className="flex items-center space-x-2">
                  <Bell size={16} />
                  <span className="text-sm">Notifications</span>
                </div>
                <div className="h-5 w-5 rounded-full bg-green-500 flex items-center justify-center text-white text-xs">
                  3
                </div>
              </a>
            </div>
            <div className={`mt-3 pt-3 border-t ${borderColor}`}>
              <button className={`flex items-center space-x-2 w-full p-2 rounded-md ${hoverBgColor} text-red-400`}>
                <ArrowRight size={16} />
                <span className="text-sm">Sign out</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notifications Popup */}
      {showNotifications && (
        <div className={`fixed top-16 right-24 w-80 ${cardBgColor} rounded-lg shadow-lg z-20 overflow-hidden border ${borderColor}`}>
          <div className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold">Notifications</h3>
              <button className={`text-xs ${highlightColor}`}>Mark all as read</button>
            </div>
            <div className="space-y-2">
              {notifications.map(notification => (
                <div key={notification.id} className={`p-2 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                  <div className="flex items-start">
                    <div className="mr-2 mt-1">
                      {notification.type === 'alert' && <AlertTriangle size={16} className="text-yellow-400" />}
                      {notification.type === 'info' && <Bell size={16} className="text-blue-400" />}
                      {notification.type === 'success' && <Shield size={16} className="text-green-400" />}
                    </div>
                    <div>
                      <p className="text-sm">{notification.message}</p>
                      <p className={`text-xs ${mutedTextColor} mt-1`}>{notification.time}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3 pt-3 border-t ${borderColor}">
              <a href="#" className={`text-xs ${highlightColor} hover:underline`}>View all notifications →</a>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-grow mt-16 container mx-auto px-4 py-6">
        {activeTab === 'network-test' && (
          <NetworkTestTab />
        )}
        
        {activeTab === 'speed-test' && (
          <SpeedTestTab />
        )}
      </main>

      {/* Advanced Footer */}
      <footer className={`${navBgColor} py-8 shadow-inner`}>
        <div className="container mx-auto px-4">
          {/* Top Section with Stats */}
          <div className={`grid grid-cols-1 md:grid-cols-3 gap-6 p-4 mb-6 rounded-lg ${darkMode ? 'bg-gray-750' : 'bg-gray-50'} border ${borderColor}`}>
            <div className="text-center md:text-left">
              <p className={`text-sm ${mutedTextColor} mb-1`}>Network Status</p>
              <div className="flex items-center justify-center md:justify-start">
                <div className={`h-3 w-3 rounded-full ${networkStatus.status === 'online' ? 'bg-green-400' : 'bg-yellow-400'} mr-2`}></div>
                <p className="font-medium">{networkStatus.status === 'online' ? 'All Systems Operational' : 'Partially Degraded'}</p>
              </div>
            </div>
            <div className="text-center">
              <p className={`text-sm ${mutedTextColor} mb-1`}>Current Time</p>
              <p className="font-medium">{currentTime.toLocaleTimeString()} - {currentTime.toLocaleDateString()}</p>
            </div>
            <div className="text-center md:text-right">
              <p className={`text-sm ${mutedTextColor} mb-1`}>Connected Users</p>
              <p className="font-medium">1,283 <span className={`text-xs ${darkMode ? 'text-green-400' : 'text-green-600'}`}>(+12% today)</span></p>
            </div>
          </div>

          {/* Main Footer Content */}
          <div className="flex flex-col md:flex-row mb-8">
            {/* Logo and Info */}
            <div className="mb-6 md:mb-0 md:w-1/3 pr-4">
              <div className="flex items-center space-x-2 mb-3">
                <div className="h-10 w-10 rounded-md bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center">
                  <Globe size={20} className="text-white" />
                </div>
                <span className={`font-bold text-xl ${highlightColor}`}>CognitoNet</span>
              </div>
              <p className={`text-sm ${mutedTextColor} mb-4`}>Comprehensive network diagnostic and performance monitoring tools for IT professionals and network administrators.</p>
              <div className="flex space-x-4">
                <a href="#" className={`p-2 rounded-full ${hoverBgColor}`}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                  </svg>
                </a>
                <a href="#" className={`p-2 rounded-full ${hoverBgColor}`}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                  </svg>
                </a>
                <a href="#" className={`p-2 rounded-full ${hoverBgColor}`}>
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c5.51 0 10-4.48 10-10S17.51 2 12 2zm6.605 4.61a8.502 8.502 0 011.93 5.314c-.281-.054-3.101-.629-5.943-.271-.065-.141-.12-.293-.184-.445a25.416 25.416 0 00-.564-1.236c3.145-1.28 4.577-3.124 4.761-3.362zM12 3.475c2.17 0 4.154.813 5.662 2.148-.152.216-1.443 1.941-4.48 3.08-1.399-2.57-2.95-4.675-3.189-5A8.687 8.687 0 0112 3.475zm-3.633.803a53.896 53.896 0 013.167 4.935c-3.992 1.063-7.517 1.04-7.896 1.04a8.581 8.581 0 014.729-5.975zM3.453 12.01v-.26c.37.01 4.512.065 8.775-1.215.25.477.477.965.694 1.453-.109.033-.228.065-.336.098-4.404 1.42-6.747 5.303-6.942 5.629a8.522 8.522 0 01-2.19-5.705zM12 20.547a8.482 8.482 0 01-5.239-1.8c.152-.315 1.888-3.656 6.703-5.337.022-.01.033-.01.054-.022a35.318 35.318 0 011.823 6.475 8.4 8.4 0 01-3.341.684zm4.761-1.465c-.086-.52-.542-3.015-1.659-6.084 2.679-.423 5.022.271 5.314.369a8.468 8.468 0 01-3.655 5.715z" clipRule="evenodd" />
                  </svg>
                </a>
              </div>
            </div>
            
            {/* Footer Nav Columns */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:w-2/3">
              <div>
                <h3 className="font-semibold mb-4">Product</h3>
                <ul className={`space-y-2 text-sm ${mutedTextColor}`}>
                  <li><a href="#" className="hover:underline">Features</a></li>
                  <li><a href="#" className="hover:underline">Pricing</a></li>
                  <li><a href="#" className="hover:underline">Updates</a></li>
                  <li><a href="#" className="hover:underline">Beta Program</a></li>
                  <li><a href="#" className="hover:underline">Roadmap</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Support</h3>
                <ul className={`space-y-2 text-sm ${mutedTextColor}`}>
                  <li><a href="#" className="hover:underline">Documentation</a></li>
                  <li><a href="#" className="hover:underline">Tutorials</a></li>
                  <li><a href="#" className="hover:underline">Community Forum</a></li>
                  <li><a href="#" className="hover:underline">Contact Support</a></li>
                  <li><a href="#" className="hover:underline">FAQ</a></li>
                </ul>
              </div>
              <div>
                <h3 className="font-semibold mb-4">Company</h3>
                <ul className={`space-y-2 text-sm ${mutedTextColor}`}>
                  <li><a href="#" className="hover:underline">About Us</a></li>
                  <li><a href="#" className="hover:underline">Blog</a></li>
                  <li><a href="#" className="hover:underline">Careers</a></li>
                  <li><a href="#" className="hover:underline">Legal</a></li>
                  <li><a href="#" className="hover:underline">Contact</a></li>
                </ul>
              </div>
            </div>
          </div>
          
          {/* Bottom Footer */}
          <div className={`flex flex-col md:flex-row justify-between pt-6 border-t ${borderColor}`}>
            <p className={`text-sm ${mutedTextColor} mb-4 md:mb-0`}>
              © {new Date().getFullYear()} CognitoNet. All rights reserved.
            </p>
            <div className="flex flex-wrap gap-4 text-sm">
              <a href="#" className={`${mutedTextColor} hover:underline`}>Privacy Policy</a>
              <a href="#" className={`${mutedTextColor} hover:underline`}>Terms of Service</a>
              <a href="#" className={`${mutedTextColor} hover:underline`}>Cookie Policy</a>
              <a href="#" className={`${mutedTextColor} hover:underline`}>Security</a>
            </div>
          </div>
        </div>
      </footer>
      
      {/* Additional Overlay for Mobile Menu */}
      {showMobileMenu && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-30" 
          onClick={toggleMobileMenu}
        ></div>
      )}
    </div>
  );
};

export default CognitoNet;